import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface State {
  readonly error: Error | null;
}

/**
 * A crash must not take the user's work with it silently: the boundary offers
 * to download the current project before reloading.
 */
export class ErrorBoundary extends Component<
  { readonly children: ReactNode; readonly onRecover: () => void },
  State
> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Brickwall crashed', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return (
      <div className="crash">
        <div className="card" role="alert">
          <h2>Something went wrong</h2>
          <p>
            The editor hit an error and stopped. Your work is still in memory — download it before
            reloading.
          </p>
          <pre>{error.message}</pre>
          <div className="actions">
            <button className="btn primary" onClick={this.props.onRecover}>
              Download the project
            </button>
            <button className="btn" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
